import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import OptimizedImage from '../ui/Image';
import './Header.css';

const Header = ({ isMenuOpen, setIsMenuOpen }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Close menu when location changes
  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname, setIsMenuOpen]);

  // Handle window resize to reset navigation display
  useEffect(() => {
    const handleResize = () => {
      const nav = document.querySelector('.nav');
      if (nav) {
        if (window.innerWidth > 768) {
          // Desktop: ensure nav is visible and reset styles
          nav.style.display = 'flex';
          nav.classList.remove('nav--open');
          setIsMenuOpen(false);
        } else {
          // Mobile: hide nav unless menu is open
          if (!isMenuOpen) {
            nav.style.display = 'none';
          }
        }
      }
    };

    window.addEventListener('resize', handleResize);

    // Call once on mount
    handleResize();

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [isMenuOpen]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMenuOpen) {
      document.body.classList.add('menu-open');
    } else {
      document.body.classList.remove('menu-open');
    }

    return () => {
      document.body.classList.remove('menu-open');
    };
  }, [isMenuOpen]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isMenuOpen && !event.target.closest('.nav') && !event.target.closest('.nav__toggle')) {
        setIsMenuOpen(false);
      }
    };

    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isMenuOpen, setIsMenuOpen]);

  const toggleMenu = () => {
    const newMenuState = !isMenuOpen;
    setIsMenuOpen(newMenuState);

    // Force show/hide navigation on mobile
    const nav = document.querySelector('.nav');
    if (nav && window.innerWidth <= 768) {
      if (newMenuState) {
        nav.style.display = 'block';
        // Small timeout to ensure display is set before animation
        setTimeout(() => {
          nav.classList.add('nav--open');
        }, 10);
      } else {
        nav.classList.remove('nav--open');
        // Wait for animation to complete before hiding
        setTimeout(() => {
          nav.style.display = 'none';
        }, 300);
      }
    }
  };

  const closeMenu = () => {
    setIsMenuOpen(false);

    // Force hide navigation on mobile
    const nav = document.querySelector('.nav');
    if (nav && window.innerWidth <= 768) {
      nav.classList.remove('nav--open');
      setTimeout(() => {
        nav.style.display = 'none';
      }, 300);
    }
  };

  const handleLinkClick = () => {
    closeMenu();
    // Ensure smooth scroll to top with a small delay to allow menu close animation
    setTimeout(() => {
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: 'smooth'
      });
    }, 100);
  };

  const isActive = (path) => location.pathname === path;
  const isServicesActive = () => location.pathname.startsWith('/services');
  const isCaseStudiesPage = () => location.pathname.startsWith('/case-studies');

  return (
    <header className={`header ${isScrolled ? 'header--scrolled' : ''}`}>
      <div className="header__content">
        {!isCaseStudiesPage() && (
          <Link to="/" className="header__logo" onClick={handleLinkClick}>
            <OptimizedImage
              src={isScrolled ? "/cospin-logo-blue.png" : "/cospin-logo.png"}
              alt="Cospin"
              className="header__logo-img"
            />
          </Link>
        )}

        <nav className={`nav ${isMenuOpen ? 'nav--open' : ''}`}>
          <ul className="nav__list">
            <li className="nav__item">
              <Link
                to="/"
                className={`nav__link ${isActive('/') ? 'nav__link--active' : ''}`}
                onClick={handleLinkClick}
              >
                Home
              </Link>
            </li>
            <li className="nav__item">
              <Link
                to="/about"
                className={`nav__link ${isActive('/about') ? 'nav__link--active' : ''}`}
                onClick={handleLinkClick}
              >
                About
              </Link>
            </li>
            <li className="nav__item">
              <Link
                to="/services"
                className={`nav__link ${isServicesActive() ? 'nav__link--active' : ''}`}
                onClick={handleLinkClick}
              >
                Services
              </Link>
            </li>

            <li className="nav__item">
              <a
                href="https://www.kushikid.com/"
                className="nav__link"
                target="_blank"
                rel="noopener noreferrer"
                onClick={handleLinkClick}
              >
                Products
              </a>
            </li>
            <li className="nav__item">
              <Link
                to="/careers"
                className={`nav__link ${isActive('/careers') ? 'nav__link--active' : ''}`}
                onClick={handleLinkClick}
              >
                Careers
              </Link>
            </li>
            <li className="nav__item">
              <Link
                to="/contact"
                className={`nav__link ${isActive('/contact') ? 'nav__link--active' : ''}`}
                onClick={handleLinkClick}
              >
                Contact
              </Link>
            </li>
          </ul>
        </nav>

        <button
          className="nav__toggle"
          onClick={toggleMenu}
          aria-label="Toggle navigation"
        >
          {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>
    </header>
  );
};

export default Header;
